import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import secureLocalStorage from 'react-secure-storage';
import auth from '../../utils/authConstants';

interface SideMenuProps {
  isOpen: boolean;
  onToggle: () => void;
}

const SideMenu = ({ isOpen, onToggle }: SideMenuProps) => {
  const location = useLocation();
  const isPlano = location.pathname.startsWith('/planogram');
  const isSign = location.pathname.startsWith('/signs');

  const [planoOpen, setPlanoOpen] = useState(isPlano);
  const [signsOpen, setSignsOpen] = useState(isSign);

  useEffect(() => {
    if (isPlano) setPlanoOpen(true);
    if (isSign) setSignsOpen(true);
  }, [location.pathname, isPlano, isSign]);

  const storeNumber = window.localStorage.getItem('storeNumber') || '';
  const paddedStore = storeNumber.padStart(5, '0');

  const handlePdf = (filePath: string) => {
    const url = `${auth.REACT_APP_SERVER_API_URL}/get-storage-file?fileName=${filePath}`;
    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${secureLocalStorage.getItem('access_token')}`,
      },
    })
      .then(r => r.blob())
      .then(blob => window.open(URL.createObjectURL(blob), '_blank'))
      .catch(err => console.error('PDF fetch error:', err));
  };

  const active = (path: string) => location.pathname === path;

  const topItemCls = (highlighted: boolean) =>
    `flex items-center w-full px-4 py-3 text-sm font-semibold transition-colors whitespace-nowrap ${
      highlighted ? 'bg-[#F96302] text-white' : 'text-gray-200 hover:bg-white/10'
    }`;

  const linkCls = (path: string) =>
    `block pl-8 pr-4 py-2 text-sm transition-colors whitespace-nowrap ${
      active(path)
        ? 'bg-[#F96302]/80 text-white font-semibold'
        : 'text-gray-400 hover:bg-white/10 hover:text-white'
    }`;

  const pdfBtnCls =
    'block w-full text-left pl-8 pr-4 py-2 text-sm text-gray-400 hover:bg-white/10 hover:text-white transition-colors whitespace-nowrap';

  return (
    <div style={{ display: 'flex', flexShrink: 0, height: '100%' }}>
      {/* Sidebar panel — width animates open/closed */}
      <div
        style={{
          width: isOpen ? 220 : 0,
          overflow: 'hidden',
          transition: 'width 0.25s ease-in-out',
          background: '#1a1a1a',
          flexShrink: 0,
          height: '100%',
          overflowY: isOpen ? 'auto' : 'hidden',
        }}
      >
        {/* Inner fixed-width wrapper prevents items from squashing during animation */}
        <div style={{ width: 220 }}>
          <nav className="py-2">
            <Link to="/welcome" className={topItemCls(active('/welcome'))}>
              Welcome
            </Link>

            {/* Planograms */}
            <div>
              <button
                className={topItemCls(isPlano) + ' justify-between cursor-pointer w-full'}
                onClick={() => setPlanoOpen(o => !o)}
              >
                <span>Planograms &amp; Floorplans</span>
                <span className="text-gray-400 text-xs ml-2">{planoOpen ? '▲' : '▼'}</span>
              </button>
              {planoOpen && (
                <div className="border-l-2 border-[#F96302]/40 ml-4">
                  <Link to="/planogram_floor" className={linkCls('/planogram_floor')}>
                    New Planograms
                  </Link>
                  <Link to="/planogram_floor_all_pogs" className={linkCls('/planogram_floor_all_pogs')}>
                    All Planograms
                  </Link>
                  <Link to="/planogram_floor_ins_docs" className={linkCls('/planogram_floor_ins_docs')}>
                    Instructions &amp; Documents
                  </Link>
                  <div className="mx-4 my-1 border-t border-white/10" />
                  <button className={pdfBtnCls} onClick={() => handlePdf(`store_doc/layouts/fpmdse/${paddedStore}fpmdse.pdf`)}>
                    Sales Floorplan
                  </button>
                  <button className={pdfBtnCls} onClick={() => handlePdf(`store_doc/layouts/fdpl/${paddedStore}fdpl.pdf`)}>
                    Cross Merch Floorplan
                  </button>
                  <button className={pdfBtnCls} onClick={() => handlePdf(`store_doc/layouts/fdloc/${paddedStore}fdloc.pdf`)}>
                    Location Codes
                  </button>
                  {/* <button className={pdfBtnCls} onClick={() => handlePdf(`store_doc/layouts/fpsign/${paddedStore}fpsign.pdf`)}>
                    Perimeter Overstock Signs
                  </button> */}
                </div>
              )}
            </div>

            {/* Signs */}
            <div>
              <button
                className={topItemCls(isSign) + ' justify-between cursor-pointer w-full'}
                onClick={() => setSignsOpen(o => !o)}
              >
                <span>Signs</span>
                <span className="text-gray-400 text-xs ml-2">{signsOpen ? '▲' : '▼'}</span>
              </button>
              {signsOpen && (
                <div className="border-l-2 border-[#F96302]/40 ml-4">
                  <Link to="/signs" className={linkCls('/signs')}>
                    Signs Home
                  </Link>
                  <Link to="/signs_circular" className={linkCls('/signs_circular')}>
                    Circular &amp; Deals
                  </Link>
                  <Link to="/signs_permanent" className={linkCls('/signs_permanent')}>
                    Permanent
                  </Link>
                  <Link to="/signs_lp_safety" className={linkCls('/signs_lp_safety')}>
                    Lp/Safety
                  </Link>
                  <Link to="/signs_misc" className={linkCls('/signs_misc')}>
                    Miscellaneous
                  </Link>
                  <div className="mx-4 my-1 border-t border-white/10" />
                  <Link to="/signs_active" className={linkCls('/signs_active')}>
                    Active Signs
                  </Link>
                  <Link to="/signs_expired" className={linkCls('/signs_expired')}>
                    Expired Signs
                  </Link>
                </div>
              )}
            </div>

            <Link to="/rebates" className={topItemCls(active('/rebates'))}>
              Rebates
            </Link>
          </nav>
        </div>
      </div>

      {/* Toggle arrow tab — always visible, hugs the right edge of the sidebar */}
      <button
        onClick={onToggle}
        title={isOpen ? 'Collapse menu' : 'Expand menu'}
        style={{
          width: 18,
          flexShrink: 0,
          background: '#F96302',
          border: 'none',
          cursor: 'pointer',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 10,
          userSelect: 'none',
        }}
      >
        {isOpen ? '◀' : '▶'}
      </button>
    </div>
  );
};

export default SideMenu;
